/** Writing a person into a project (RFC 0324 3a). */

import type { Repo } from '@automerge/automerge-repo/slim'
import { findProjectForWrite } from '../project/mutations'
import { changeProject } from '../project/scalars'
import type { PersonEntry } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/**
 * Put a person in the project's people list, or bring their entry up to date.
 *
 * Called on every comment a person writes, so it must be cheap when nothing
 * changed: it writes only when the entry is missing or a field differs. An
 * existing entry is edited in place, because re-assigning an object that is
 * already in an Automerge document throws.
 */
export async function upsertPerson(
  repo: Repo,
  projectId: string,
  person: PersonEntry,
): Promise<void> {
  const project = await findProjectForWrite(repo, projectId)
  const current = (project.doc() as { people?: Record<string, PersonEntry> })
    ?.people?.[person.id]
  if (current && samePerson(current, person)) return
  changeProject(project, (d: Draft) => {
    const doc = d as { people?: Record<string, PersonEntry> }
    if (!doc.people) doc.people = {}
    const existing = doc.people[person.id]
    if (!existing) {
      doc.people[person.id] = { ...person }
      return
    }
    existing.name = person.name
    for (const key of ['accountId', 'avatarUrl', 'tint'] as const) {
      const value = person[key]
      if (value === undefined) delete existing[key]
      else existing[key] = value
    }
  })
}

function samePerson(a: PersonEntry, b: PersonEntry): boolean {
  return (
    String(a.name) === b.name &&
    (a.accountId == null ? undefined : String(a.accountId)) === b.accountId &&
    (a.avatarUrl == null ? undefined : String(a.avatarUrl)) === b.avatarUrl &&
    (a.tint == null ? undefined : String(a.tint)) === b.tint
  )
}
